import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import AnnouncementsManager from "@/components/dashboard/AnnouncementsManager";
import RecognitionsManager from "@/components/dashboard/RecognitionsManager";
import UsersManager from "@/components/dashboard/UsersManager";
import DownloadsManager from "@/components/dashboard/DownloadsManager";
import NewsManager from "@/components/dashboard/NewsManager";
import ChampionshipsManager from "@/components/dashboard/ChampionshipsManager";
import MediaManager from "@/components/dashboard/MediaManager";
import ContactMessagesManager from "@/components/dashboard/ContactMessagesManager";
import ZonesManager from "@/components/dashboard/ZonesManager";
import CertificatesManager from "@/components/dashboard/CertificatesManager";
import ApplicationDetailModal from "@/components/dashboard/ApplicationDetailModal";
import api from "@/lib/api";
import {
  LayoutDashboard, ClipboardList, Users, Megaphone, ShieldCheck,
  Download, Newspaper, Trophy, Eye, Camera, Mail, MapPin, Award,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "applications", label: "Applications", icon: ClipboardList },
  { key: "messages", label: "Messages", icon: Mail },
  { key: "users", label: "Coaches & Officials", icon: Users },
  { key: "zones", label: "Zones", icon: MapPin },
  { key: "championships", label: "Championships", icon: Trophy },
  { key: "certificates", label: "Certificates", icon: Award },
  { key: "news", label: "News", icon: Newspaper },
  { key: "media", label: "Media", icon: Camera },
  { key: "downloads", label: "Downloads", icon: Download },
  { key: "announcements", label: "Top Strip", icon: Megaphone },
  { key: "recognitions", label: "Recognitions", icon: ShieldCheck },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [memberships, setMemberships] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [open, setOpen] = useState(null);

  const refreshApps = () => {
    api.get("/memberships").then((r) => setMemberships(r.data)).catch(() => {});
    api.get("/affiliations").then((r) => setAffiliations(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/stats/federation").then((r) => setStats(r.data)); refreshApps(); }, []);

  const pendingMem = memberships.filter((m) => (m.status || "pending") === "pending").length;
  const pendingAff = affiliations.filter((a) => (a.status || "pending") === "pending").length;

  return (
    <DashboardShell title="Federation Administration" eyebrow="Admin Console">
      {/* Tab bar */}
      <div className="bg-white border border-slate-200 rounded-md p-1.5 mb-6 overflow-x-auto" data-testid="admin-tabs">
        <div className="flex items-center gap-1 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded text-xs uppercase tracking-widest font-semibold transition whitespace-nowrap ${
                  active ? "bg-nkf-blue text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
                {t.key === "applications" && (pendingMem + pendingAff) > 0 && (
                  <span className={`ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-nkf-gold text-nkf-navy" : "bg-nkf-red text-white"}`}>{pendingMem + pendingAff}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "overview" && (
        <div className="space-y-6 animate-fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["coaches", "Coaches"],
              ["officials", "Officials"],
              ["national_championships", "National Events"],
              ["recognitions", "Recognitions"],
            ].map(([k, l]) => (
              <div key={k} className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{l}</div>
                <div className="font-display text-2xl text-nkf-blue mt-1 tabular-nums">{(stats[k] || 0).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PendingCard title="Pending Memberships" count={pendingMem} onClick={() => setTab("applications")} />
            <PendingCard title="Pending Affiliations" count={pendingAff} onClick={() => setTab("applications")} />
          </div>
          <div className="p-5 bg-nkf-blue text-white rounded-md flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-nkf-gold">Quick Actions</div>
              <div className="font-display text-lg">Use the tabs above to manage every part of the federation site.</div>
            </div>
          </div>
        </div>
      )}

      {tab === "applications" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 animate-fade-up">
          <ApplicationsList title="Membership Applications" rows={memberships} kind="membership" onView={(id) => setOpen({ kind: "membership", id })} />
          <ApplicationsList title="Affiliation Applications" rows={affiliations} kind="affiliation" onView={(id) => setOpen({ kind: "affiliation", id })} />
        </div>
      )}

      {tab === "users" && <div className="animate-fade-up"><UsersManager /></div>}
      {tab === "zones" && <div className="animate-fade-up"><ZonesManager /></div>}
      {tab === "championships" && <div className="animate-fade-up"><ChampionshipsManager /></div>}
      {tab === "certificates" && <div className="animate-fade-up"><CertificatesManager /></div>}
      {tab === "news" && <div className="animate-fade-up"><NewsManager /></div>}
      {tab === "media" && <div className="animate-fade-up"><MediaManager /></div>}
      {tab === "downloads" && <div className="animate-fade-up"><DownloadsManager /></div>}
      {tab === "announcements" && <div className="animate-fade-up"><AnnouncementsManager /></div>}
      {tab === "recognitions" && <div className="animate-fade-up"><RecognitionsManager /></div>}
      {tab === "messages" && <div className="animate-fade-up"><ContactMessagesManager /></div>}

      {open && (
        <ApplicationDetailModal kind={open.kind} id={open.id} onClose={() => setOpen(null)} onUpdated={refreshApps} />
      )}
    </DashboardShell>
  );
}

function PendingCard({ title, count, onClick }) {
  return (
    <button onClick={onClick} className="text-left bg-white border border-slate-200 rounded-md p-5 hover:border-nkf-blue/40 transition group">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{title}</div>
      <div className="flex items-baseline justify-between mt-1">
        <div className="font-display text-3xl text-nkf-blue tabular-nums">{count}</div>
        <span className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold group-hover:underline">Review →</span>
      </div>
    </button>
  );
}

function ApplicationsList({ title, rows, kind, onView }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg text-nkf-blue">{title}</h3>
        <div className="text-[11px] uppercase tracking-widest text-slate-400">{rows.length} total</div>
      </div>
      <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
        {rows.map((r) => (
          <button key={r.id} onClick={() => onView(r.id)} data-testid={`view-${kind}-${r.id}`} className="w-full text-left py-2.5 px-1 flex items-center justify-between gap-3 hover:bg-slate-50 transition rounded">
            <div className="min-w-0">
              <div className="font-semibold text-nkf-blue truncate text-sm">{kind === "membership" ? r.applicant_name : r.organisation_name}</div>
              <div className="text-[11px] text-slate-500 truncate">{r.application_no} · {r.state || "—"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge status={r.status} />
              <Eye className="h-3.5 w-3.5 text-slate-400" />
            </div>
          </button>
        ))}
        {!rows.length && <div className="py-8 text-center text-slate-400 text-sm">No applications yet.</div>}
      </div>
    </div>
  );
}
function Badge({ status }) {
  const c = status === "approved" ? "bg-emerald-100 text-emerald-700" : status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800";
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${c}`}>{status || "pending"}</span>;
}
